/**
 * Sticky ATC — Mobile bottom bar and purchase-overlay priority.
 *
 * The stable body attribute is deliberately set from JS rather than relying on
 * :has(), so Shopify Inbox also yields in browsers without relational selectors.
 * Klaviyo inline newsletter forms remain untouched: only a semantic dialog or a
 * fixed ancestor containing a Klaviyo form is suppressed on mobile product pages.
 */
(function () {
  "use strict";

  var MOBILE_QUERY = "(max-width: 640px)";
  var CHAT_SELECTOR = [
    "shopify-agent[view='chat']",
    "#ShopifyChat",
    "#shopify-chat",
    "[data-shopify-chat]"
  ].join(",");
  var KLAVIYO_FORM_SELECTOR = [
    "form.klaviyo-form",
    "[data-klaviyo-form]",
    "[data-testid^='klaviyo-form-']"
  ].join(",");
  var KLAVIYO_DIALOG_SELECTOR =
    "[role='dialog'][aria-modal='true'][data-kl-scroll-locking-modal='true']";
  var KLAVIYO_BODY_LOCK_CLASS = "klaviyo-prevent-body-scrolling";
  var KLAVIYO_BODY_LOCK_PROPERTIES = ["overflow", "overflow-x", "overflow-y"];
  var overlayStates = new WeakMap();

  function findWithin(root, selector) {
    var matches = [];
    if (!root || root.nodeType !== 1) return matches;
    if (root.matches && root.matches(selector)) matches.push(root);
    if (root.querySelectorAll) {
      matches = matches.concat(Array.prototype.slice.call(root.querySelectorAll(selector)));
    }
    return matches;
  }

  function saveAndSuppress(element, markerAttribute) {
    if (!element || overlayStates.has(element)) return;

    var styleProperties = ["opacity", "pointer-events", "visibility"];
    var styleState = {};
    styleProperties.forEach(function (property) {
      styleState[property] = {
        value: element.style.getPropertyValue(property),
        priority: element.style.getPropertyPriority(property)
      };
      element.style.setProperty(
        property,
        property === "opacity" ? "0" : (property === "pointer-events" ? "none" : "hidden"),
        "important"
      );
    });

    overlayStates.set(element, {
      markerAttribute: markerAttribute,
      hadAriaHidden: element.hasAttribute("aria-hidden"),
      ariaHidden: element.getAttribute("aria-hidden"),
      hadInert: element.hasAttribute("inert"),
      inert: "inert" in element ? element.inert : null,
      style: styleState
    });

    element.setAttribute(markerAttribute, "true");
    element.setAttribute("aria-hidden", "true");
    element.setAttribute("inert", "");
    if ("inert" in element) element.inert = true;
  }

  function restoreSuppressed(element) {
    var state = overlayStates.get(element);
    if (!state) return;

    Object.keys(state.style).forEach(function (property) {
      var previous = state.style[property];
      if (previous.value) {
        element.style.setProperty(property, previous.value, previous.priority);
      } else {
        element.style.removeProperty(property);
      }
    });

    if (state.hadAriaHidden) {
      element.setAttribute("aria-hidden", state.ariaHidden);
    } else {
      element.removeAttribute("aria-hidden");
    }
    if (state.hadInert) {
      element.setAttribute("inert", "");
      if ("inert" in element) element.inert = state.inert;
    } else {
      element.removeAttribute("inert");
      if ("inert" in element) element.inert = false;
    }
    element.removeAttribute(state.markerAttribute);
    overlayStates.delete(element);
  }

  function getKlaviyoDialogRoot(form) {
    var semanticDialog = form.closest(
      "[role='dialog'][aria-modal='true'],[role='dialog'],[aria-modal='true']"
    );
    var current = semanticDialog || form;
    var fixedAncestor = null;

    while (current && current !== document.body) {
      if (window.getComputedStyle(current).position === "fixed") {
        fixedAncestor = current;
      }
      current = current.parentElement;
    }

    return fixedAncestor || semanticDialog;
  }

  function unlockKlaviyoBody(body) {
    if (!body.classList.contains(KLAVIYO_BODY_LOCK_CLASS)) return null;

    var state = { style: {}, declaredProperties: [] };

    // Read the full CSSOM snapshot before removing the shorthand. In browsers,
    // removing `overflow` can also clear its serialized longhands, so a
    // read-and-remove loop would lose the original overflow-x/overflow-y lock.
    KLAVIYO_BODY_LOCK_PROPERTIES.forEach(function (property) {
      state.style[property] = {
        value: body.style.getPropertyValue(property),
        priority: body.style.getPropertyPriority(property)
      };
    });

    if (typeof body.style.length === "number" && typeof body.style.item === "function") {
      for (var index = 0; index < body.style.length; index += 1) {
        var declaredProperty = body.style.item(index);
        if (
          KLAVIYO_BODY_LOCK_PROPERTIES.indexOf(declaredProperty) !== -1 &&
          state.declaredProperties.indexOf(declaredProperty) === -1
        ) {
          state.declaredProperties.push(declaredProperty);
        }
      }
    }
    if (!state.declaredProperties.length) {
      state.declaredProperties = KLAVIYO_BODY_LOCK_PROPERTIES.filter(function (property) {
        return Boolean(state.style[property].value);
      });
    }

    KLAVIYO_BODY_LOCK_PROPERTIES.forEach(function (property) {
      body.style.removeProperty(property);
    });
    body.classList.remove(KLAVIYO_BODY_LOCK_CLASS);
    return state;
  }

  function keepKlaviyoBodyUnlocked(body, state) {
    // A third-party overlay (product zoom, samples drawer, consent UI) may set
    // inline overflow while the hidden Klaviyo dialog still exists. Only act
    // when Klaviyo actually re-applies its own named lock, and only clear the
    // inline values captured with that lock.
    if (!body.classList.contains(KLAVIYO_BODY_LOCK_CLASS)) return;

    body.classList.remove(KLAVIYO_BODY_LOCK_CLASS);
    (state && state.declaredProperties || KLAVIYO_BODY_LOCK_PROPERTIES).forEach(function (property) {
      var previous = state && state.style[property];
      if (!previous || !previous.value) return;
      if (
        body.style.getPropertyValue(property) === previous.value &&
        body.style.getPropertyPriority(property) === previous.priority
      ) {
        body.style.removeProperty(property);
      }
    });
  }

  function relockKlaviyoBody(body, state) {
    if (!state) return;

    body.classList.add(KLAVIYO_BODY_LOCK_CLASS);

    // Another overlay can acquire its own inline scroll lock while Klaviyo is
    // suppressed on mobile. The Klaviyo class is enough to reactivate its CSS
    // lock; only restore the captured inline declarations when the overflow
    // slots are still free, otherwise preserve the newer owner's values.
    var hasConcurrentInlineOverflow = KLAVIYO_BODY_LOCK_PROPERTIES.some(function (property) {
      return Boolean(body.style.getPropertyValue(property));
    });
    if (hasConcurrentInlineOverflow) return;

    state.declaredProperties.forEach(function (property) {
      var previous = state.style[property];
      if (previous.value) {
        body.style.setProperty(property, previous.value, previous.priority);
      }
    });
  }

  if (typeof module !== "undefined" && module.exports) {
    module.exports = {
      unlockKlaviyoBody: unlockKlaviyoBody,
      keepKlaviyoBodyUnlocked: keepKlaviyoBodyUnlocked,
      relockKlaviyoBody: relockKlaviyoBody
    };
  }
  if (typeof document === "undefined" || typeof window === "undefined") return;

  document.addEventListener("DOMContentLoaded", function () {
    var addCartBtn = document.getElementById("AddToCart");
    var priceEl = document.querySelector(".product__info__price");
    if (!addCartBtn || !priceEl) return;

    var mobileMedia = window.matchMedia(MOBILE_QUERY);
    var bar = document.createElement("div");
    bar.className = "sticky-atc-bar";
    bar.innerHTML = '<div class="sticky-atc-bar__content">' +
      '<div class="sticky-atc-bar__price"></div>' +
      '<button class="sticky-atc-bar__button button-small button-inverted upper t13-14"></button>' +
      "</div>";
    document.body.appendChild(bar);

    var barPrice = bar.querySelector(".sticky-atc-bar__price");
    var barBtn = bar.querySelector(".sticky-atc-bar__button");
    var klaviyoBodyLockState = null;

    function isMobileProduct() {
      // This asset is loaded only by the product purchase section. Avoid using
      // the body template class because alternate templates render values such
      // as "product-trenzar" rather than the exact class "product".
      return mobileMedia.matches;
    }

    function syncChatPriority(root) {
      var stickyVisible = isMobileProduct() && bar.classList.contains("visible");
      var scope = root && root.nodeType === 1 ? root : document.body;

      if (stickyVisible) {
        findWithin(scope, CHAT_SELECTOR).forEach(function (chat) {
          saveAndSuppress(chat, "data-marlot-chat-yield");
        });
      } else {
        document.querySelectorAll("[data-marlot-chat-yield='true']").forEach(restoreSuppressed);
      }
    }

    function syncKlaviyoPriority(root) {
      var scope = root && root.nodeType === 1 ? root : document.body;

      if (isMobileProduct()) {
        findWithin(scope, KLAVIYO_DIALOG_SELECTOR).forEach(function (dialog) {
          saveAndSuppress(dialog, "data-marlot-klaviyo-product-dialog");
        });
        findWithin(scope, KLAVIYO_FORM_SELECTOR).forEach(function (form) {
          var dialog = getKlaviyoDialogRoot(form);
          if (dialog) saveAndSuppress(dialog, "data-marlot-klaviyo-product-dialog");
        });
      } else {
        document.querySelectorAll("[data-marlot-klaviyo-product-dialog='true']").forEach(restoreSuppressed);
      }
    }

    function syncKlaviyoBodyPriority() {
      var hasSuppressedDialog = !!document.querySelector(
        "[data-marlot-klaviyo-product-dialog='true']"
      );

      if (isMobileProduct() && hasSuppressedDialog) {
        if (!klaviyoBodyLockState) {
          klaviyoBodyLockState = unlockKlaviyoBody(document.body);
        } else {
          // Klaviyo can re-apply its lock after a React render. Keep the body
          // usable while retaining the original values for desktop restore.
          keepKlaviyoBodyUnlocked(document.body, klaviyoBodyLockState);
        }
      } else if (!isMobileProduct() && klaviyoBodyLockState) {
        relockKlaviyoBody(document.body, klaviyoBodyLockState);
        klaviyoBodyLockState = null;
      } else if (isMobileProduct() && !hasSuppressedDialog && klaviyoBodyLockState) {
        // The popup was removed while suppressed. Its lock must not outlive it,
        // and must not be restored on a later viewport change.
        keepKlaviyoBodyUnlocked(document.body, klaviyoBodyLockState);
        klaviyoBodyLockState = null;
      }
    }

    function syncPurchasePriority() {
      var stickyVisible = isMobileProduct() && bar.classList.contains("visible");
      if (stickyVisible) {
        document.body.setAttribute("data-sticky-atc-visible", "true");
      } else {
        document.body.removeAttribute("data-sticky-atc-visible");
      }
      syncChatPriority(document.body);
      syncKlaviyoPriority(document.body);
      syncKlaviyoBodyPriority();
    }

    function syncPrice() {
      barPrice.textContent = priceEl.textContent.trim();
    }

    function syncButton() {
      var isAvailable = addCartBtn.classList.contains("product-available");
      var sourceLabel = addCartBtn.querySelector(isAvailable ? ".text-available" : ".text-unavailable");
      if (!sourceLabel) return;
      barBtn.textContent = sourceLabel.textContent.trim();
      barBtn.disabled = !isAvailable;
    }

    barBtn.addEventListener("click", function () {
      addCartBtn.click();
    });

    syncPrice();
    syncButton();

    var priceObserver = new MutationObserver(syncPrice);
    priceObserver.observe(priceEl, { childList: true, subtree: true, characterData: true });

    var btnObserver = new MutationObserver(syncButton);
    btnObserver.observe(addCartBtn, { attributes: true, attributeFilter: ["class"] });

    var injectedOverlayObserver = new MutationObserver(function (records) {
      records.forEach(function (record) {
        record.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          syncChatPriority(node);
          syncKlaviyoPriority(node);
        });
      });
      syncKlaviyoBodyPriority();
    });
    injectedOverlayObserver.observe(document.body, { childList: true, subtree: true });

    var klaviyoBodyLockObserver = new MutationObserver(syncKlaviyoBodyPriority);
    klaviyoBodyLockObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ["class", "style"]
    });

    var buyBox = document.querySelector(".button-add-wrapper");
    if (buyBox && window.IntersectionObserver) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          bar.classList.toggle("visible", isMobileProduct() && !entry.isIntersecting);
        });
        syncPurchasePriority();
      }, { threshold: 0 });
      observer.observe(buyBox);
    }

    function syncViewportPriority() {
      if (!isMobileProduct()) bar.classList.remove("visible");
      syncPurchasePriority();
    }

    if (mobileMedia.addEventListener) {
      mobileMedia.addEventListener("change", syncViewportPriority);
    } else if (mobileMedia.addListener) {
      mobileMedia.addListener(syncViewportPriority);
    }

    syncPurchasePriority();
  });
})();
